import { UUID } from 'node:crypto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import { UserService } from './user.service';

export interface IIncidentCodePostRequest extends IBaseIncidentCode {
	alias: string;
}

export interface IIncidentCodePutRequest extends IBaseIncidentCode {
	// No additional properties
}

export interface IBaseIncidentCode {
	name: string;
	group: string;
	isActive: boolean;
}

export interface IIncidentCode extends IBaseIncidentCode {
	id: UUID;
	alias: string;
}

export interface IIncidentCodeResponse {
	result: IIncidentCode[];
}

export interface IIncidentCodeByIdResponse {
	result: IIncidentCode;
}

export interface IIncidentCodesByGroup {
	group: string;
	codes: IIncidentCode[];
}

@Injectable({
	providedIn: 'root',
})
export class IncidentCodeService {
	constructor(
		private http: HttpClient,
		private userService: UserService,
		private translate: TranslateService,
	) {}

	create(incidentCode: IIncidentCode): Observable<IIncidentCode> {
		const request: IIncidentCodePostRequest = {
			name: incidentCode.name,
			group: incidentCode.group,
			isActive: incidentCode.isActive,
			alias: incidentCode.alias,
		};
		return this.http.post<IIncidentCode>(this.getUrl(), request, {
			headers: this.userService.getHeaders(),
		});
	}

	update(incidentCode: IIncidentCode): Observable<IIncidentCode> {
		const request: IIncidentCodePutRequest = {
			name: incidentCode.name,
			group: incidentCode.group,
			isActive: incidentCode.isActive,
		};

		return this.http.put<IIncidentCode>(this.getUrl(incidentCode.id), request, {
			headers: this.userService.getHeaders(),
		});
	}

	getAll(): Observable<IIncidentCode[]> {
		return this.http.get<IIncidentCodeResponse>(this.getUrl(), { headers: this.userService.getHeaders() }).pipe(map((response) => response.result));
	}

	getById(id: UUID): Observable<IIncidentCode> {
		return this.http
			.get<IIncidentCodeByIdResponse>(this.getUrl(id), {
				headers: this.userService.getHeaders(),
			})
			.pipe(map((response) => response.result));
	}

	delete(id: string): Observable<any> {
		return this.http.delete(this.getUrl(id), {
			headers: this.userService.getHeaders(),
		});
	}

	getUrl(id?: string | null) {
		return `${environment.awsEnvUri}/mes/incidentcodes${id ? `/${id}` : ''}`;
	}

	filterIncidentCodesByGroups(codes: IIncidentCode[], incidentCodeGroups: string[] | null | undefined): IIncidentCodesByGroup[] {
		if (!incidentCodeGroups?.length) {
			return [];
		}

		const selectedGroups = new Set(incidentCodeGroups);
		return this.groupIncidentCodes(codes).filter((group) => selectedGroups.has(group.group));
	}

	groupIncidentCodes(codes: IIncidentCode[]): IIncidentCodesByGroup[] {
		const activeCodes = codes.filter((e) => e.isActive);
		const defaultGroupName = this.translate.instant('mes.incident-code.shared.default-group');
		const grouped = activeCodes.reduce(
			(acc, code) => {
				const group = code.group || defaultGroupName;
				(acc[group] ??= []).push(code);
				return acc;
			},
			{} as Record<string, IIncidentCode[]>,
		);
		return Object.entries(grouped).map(([group, codes]) => ({ group, codes }));
	}
}
